<template>
  <ReuseableModal
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    :title="modalTitle"
    :submitButtonText="submitText"
    @submit="handleSubmit"
    :loading="isLoading"
    :isLarge="true"
    :performance="true"
  >
    <template #default>
      <div class="modal-body-container">
        <div class="form-scroll-container" style="padding: 12px 14px;">
          <!-- Type Dropdown -->
          <div class="mb-1">
            <CustomDropdown
              :options="criteriaNames"
              :value="form.criteriaName"
              label="Type"
              :required="true"
              placeholder="Select Type"
              @change="handleCriteriaChange"
            />
          </div>

          <!-- Title -->
          <div class="mb-3">
            <label class="form-label fw-bold mb-1 text-start">
              Title <span class="text-danger"> {{ form.type === 'Ticket' ? '*' : '' }}</span>{{ form.type === 'Ticket' ? '' : '(Auto-generated)' }}
            </label>
            <input
              v-model="displayTitle"
              type="text"
              class="form-control"
              :disabled="form.type !== 'Ticket'"
              :placeholder="form.type === 'Ticket' ? 'Enter ticket title' : 'Title will be auto-generated'"
            />
          </div>

          <!-- Project Dropdown -->
          <div v-if="['Ticket', 'Lead Per Project'].includes(form.type)" class="mb-1">
            <CustomDropdown
              :options="projectOptions"
              :value="form.project"
              :label="`Project`"
              placeholder="Select Project"
              :required="form.type === 'Lead Per Project' || form.type === 'Ticket'"
              search
              @change="handleProjectChange"
            />
          </div>

          <!-- Ticket Specific Fields -->
          <div v-if="form.type === 'Ticket'">
            <div class="mb-3">
              <label class="form-label fw-bold mb-1 text-start">
                Ticket URL <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.url"
                type="url"
                class="form-control"
                placeholder="https://trello.com/c/..."
              />
            </div>
            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <label class="form-label fw-bold mb-1 text-start">
                  Duration (hrs) <span class="text-danger">*</span>
                </label>
                <input
                  v-model.number="form.duration"
                  type="number"
                  min="0"
                  step="1"
                  @keydown="preventDecimalInput"
                  class="form-control"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label fw-bold mb-1 text-start">Reopens</label>
                <input
                  v-model.number="form.reopens"
                  type="number"
                  min="0"
                  class="form-control"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label fw-bold mb-1 text-start">Deadline</label>
                <input
                  v-model.number="form.deadlineMissed"
                  type="number"
                  min="0"
                  class="form-control"
                />
              </div>
            </div>
          </div>

          <!-- PR Review/Help -->
          <div v-else-if="form.type === 'PR Review/Help'" class="mb-3 mt-3">
            <div class="d-flex align-items-center mb-3">
              <label class="form-label fw-bold mb-0 me-2 text-start">
                PRs / Helps
              </label>
              <small class="text-muted" v-if="!hasLimit">(divisible by {{ prDivisor }} → {{ prScore }} scores per {{ prDivisor }})</small>
              <small class="text-muted" v-else>(at least {{ limitValue }} → {{ prScore }} scores)</small>
               <button type="button" class="btn btn-success btn-sm" @click="addPr" style="width: 100px;">
              <i class="fa fa-plus me-1"></i>Add PR/Help
               </button>
            </div>

            <div v-for="(pr, index) in form.prs" :key="index" class="input-group mb-1">
              <input
                v-model="pr.link"
                type="text"
                class="form-control"
                placeholder="Enter PR link or help description"
              />
              <button
                type="button"
                class="btn btn-outline-danger"
                @click="removePr(index)"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
           
          </div>

          <!-- Weekly Session -->
          <div v-else-if="form.type === 'Weekly Session'" class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="form-label fw-bold mb-1 text-start">Rating </label>
              <input
                v-model.number="form.value"
                type="number"
                min="1"
                class="form-control"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label fw-bold mb-1 text-start">Date <span class="text-danger">*</span></label>
              <input v-model="form.date" type="date" class="form-control" />
            </div>
          </div>

          <!-- Attendance / Random Post -->
          <div v-else-if="['Weekly Session Attendance', 'Random Post'].includes(form.type)" class="mb-3" style="position: relative;">
            <label class="form-label fw-bold mb-1 text-start">
              {{ form.type === 'Weekly Session Attendance' ? 'Sessions Attended' : 'No. of Posts' }}
            </label>
            <div style="right: 0px; top: 0px; position: absolute;">
              <small class="text-muted " v-if="!hasLimit">
                <span v-if="form.type === 'Weekly Session Attendance'">
                  ({{ attScore.toFixed(1) }} scores each)
                </span>
                <span v-else>
                  (must be even → {{ randomScore.toFixed(1) }} score per {{ randomDivisor }})
                </span>
              </small>
              <small class="text-muted"  style=" " v-else>
                <span v-if="form.type === 'Weekly Session Attendance'">
                  (at least {{ limitValue }} → {{ attScore.toFixed(1) }} scores)
                </span>
                <span v-else>
                  (at least {{ limitValue }} → {{ randomScore.toFixed(1) }} scores)
                </span>
              </small>
            </div>
            <input
              v-model.number="form.count"
              type="number"
              :min="minCount"
              :step="countStep"
              class="form-control"
            />
          </div>

          <!-- Weekly Session Lead Info -->
          <div v-else-if="form.type === 'Weekly Session Lead'" class="alert alert-info small mb-3 py-2">
            <i class="fa fa-star me-2"></i>
            <strong>Weekly Session Lead:</strong> Automatically awards {{ leadScore }} score.
          </div>

          <!-- Reason -->
          <div class="mb-3">
            <label class="form-label fw-bold mb-1 text-start">Reason</label>
            <textarea
              v-model="form.reason"
              rows="3"
              maxlength="400"
              placeholder="Provide context or justification..."
            ></textarea>
            <div class="text-end text-muted small mt-1">{{ form.reason.length }}/400</div>
          </div>

          <!-- Score Preview -->
          <div class="score-card p-3 text-center rounded bg-light mb-0">
            <h5 class="mb-1 text-primary">
              <i class="fa fa-trophy me-2"></i>
              Calculated Score
            </h5>
            <h3 class="mb-0 fw-bold text-primary">{{ calcScore.toFixed(1) }}</h3>
          </div>
        </div>
      </div>
    </template>
  </ReuseableModal>
</template>

<script>
import CustomDropdown from 'src/components/UIComponents/CustomDropdown.vue';
import ReuseableModal from '../UIComponents/ReuseableModal.vue';
import Swal from 'sweetalert';
import firebase from '@/assets/config/firebase';
import { removeNullFields } from './performanceHelper.js';
import {formatHours, parseHours} from './performanceHelper.js';
import { logAdminActivity } from "../../utils/adminLogs.js";  
import { parseDurationString, formatDuration } from './performanceHelper.js';
import { handleActivityNotification } from '../../services/notificationService.js';

export default {
  components: {
    CustomDropdown,
    ReuseableModal,
  },
  props: {
    modelValue: Boolean,
    employee: Object,
    month: String,
    year: String,
    boards: Array,
    isAdmin: Boolean,
    existingItem: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue', 'submitted', 'updated'],
  data() {
    return {
      isLoading: false,
      formSubmitted: false,
      showTypeError: false,
      isInitializing: false,
      originalFormSnapshot: null,
      criteriaList: [],
      form: {
        criteriaName: '',
        type: '',
        title: '',
        project: '',
        boardId: '',
        url: '',
        duration: 0,
        reopens: 0,
        deadlineMissed: 0,
        count: 0,
        value: 1,
        date: '',
        level: '',
        reason: '',
        prs: [],
        selectedCriteria: null
      },
      interviewLevels: [],
    };
  },
  computed: {
    modalTitle() {
      return this.existingItem ? 'Update Performance Item' : 'Add Performance Item';
    },
    submitText() {
      return this.existingItem ? 'Update Item' : 'Submit Item';
    },
    criteriaNames() {
      return this.criteriaList.map(c => c.name);
    },
    projectOptions() {
      return this.boards.map(b => b.name) || [];
    },
    displayTitle: {
      get() {
        return this.form.type === 'Ticket' ? this.form.title : this.autoGeneratedTitle;
      },
      set(val) {
        if (this.form.type === 'Ticket') this.form.title = val;
      },
    },
    autoGeneratedTitle() {
      if (!this.form.type) return '';
      let desc = '';
      switch (this.form.type) {
        case 'PR Review/Help':
          desc = `${this.form.prs.length}`;
          break;
        case 'Weekly Session':
          desc = this.form.date ? new Date(this.form.date).toLocaleDateString('en-US') : '';
          break;
        case 'Weekly Session Attendance':
          desc = `${this.form.count}`;
          break;
        case 'Weekly Session Lead':
          desc = '';
          break;
        case 'Random Post':
          desc = `${this.form.count}`;
          break;
        case 'Interview':
          desc = this.form.level ? `${this.form.level}` : '';
          break;
        case 'Lead Per Project':
          desc = this.form.project || '';
          break;
      }
      return desc ? `${this.form.criteriaName}: ${desc}` : this.form.criteriaName;
    },
    rules() {
      return this.form.selectedCriteria.rules || {};
    },
    hasLimit() {
      return !!this.rules.limit;
    },
    limitValue() {
      return this.rules.limit || 0;
    },
    prDivisor() {
      return this.rules.countDivisor || 3;
    },
    prScore() {
      return this.form.selectedCriteria ? this.form.selectedCriteria.score || 1 : 1;
    },
    attScore() {
      return this.form.selectedCriteria ? this.form.selectedCriteria.score || 0.5 : 0.5;
    },
    randomDivisor() {
      return this.rules.countDivisor || 2;
    },
    randomScore() {
      return this.form.selectedCriteria ? this.form.selectedCriteria.score || 1 : 1;
    },
    leadScore() {
      return this.form.selectedCriteria ? this.form.selectedCriteria.rules.fixed || this.form.selectedCriteria.score || 0.5 : 0.5;
    },
    minCount() {
      if (this.hasLimit) return 0;
      return this.form.selectedCriteria ? this.form.selectedCriteria.rules.countDivisor || 1 : 1;
    },
    countStep() {
      if (this.hasLimit) return 1;
      return this.form.selectedCriteria ? this.form.selectedCriteria.rules.countDivisor || 1 : 1;
    },
    calcScore() {
      let s = 0;
      const selected = this.form.selectedCriteria;
      if (!selected) return s;
      const rules = this.rules;
      switch (this.form.type) {
        case 'Ticket':
          const hrs = this.form.duration || 0;
          const min = selected.rules.duration.min || 0;
          const max = selected.rules.duration.max - 0.01 || 0;
          s = (hrs >= min && hrs <= max) ? selected.score : 0;
          break;
        case 'PR Review/Help':
          const prCount = this.form.prs.length;
          if (rules.limit) {
            s = (prCount >= rules.limit) ? selected.score : 0;
          } else {
            const divisor = rules.countDivisor || 1;
            s = Math.floor(prCount / divisor) * selected.score;
          }
          break;
        case 'Weekly Session':
          const count = this.form.value || 0;
          if (rules.limit) {
            s = (count >= rules.limit) ? selected.score : 0;
          } else {
            const divisor = rules.countDivisor || 1;
            s = Math.floor(count / divisor) * selected.score;
          }
          break;
        case 'Weekly Session Attendance':
        case 'Random Post':
          const count2 = this.form.count || 0;
          if (rules.limit) {
            s = (count2 >= rules.limit) ? selected.score : 0;
          } else {
            const divisor = rules.countDivisor || (this.form.type === 'Random Post' ? 2 : 1);
            s = Math.floor(count2 / divisor) * selected.score;
          }
          break;
        case 'Weekly Session Lead':
          s = selected.rules.fixed || selected.score;
          break;
        case 'Interview':
          s = selected.score;
          break;
        case 'Lead Per Project':
          s = this.form.project ? selected.score : 0;
          break;
      }
      return s;
    },
  },
  watch: {
    'form.criteriaName'(newVal) {
    if (this.isInitializing) return;

    if (newVal && this.form.type !== 'Ticket') {
      this.form.title = this.autoGeneratedTitle;
    }
    this.resetDependentFields();
    this.updateAutoTitle();
  },
    'form.prs.length'() { this.updateAutoTitle(); },
    'form.count'() { this.updateAutoTitle(); },
    'form.value'() { this.updateAutoTitle(); },
    'form.date'() { this.updateAutoTitle(); },
    'form.level'() { this.updateAutoTitle(); },
    'form.project'() { this.updateAutoTitle(); },
  },
  async mounted() {
    await this.fetchCriteria();
    this.interviewLevels = this.criteriaList
      .filter(c => c.type === 'Interview')
      .map(c => c.rules.level)
      .filter(Boolean);

    if (this.existingItem) {
      this.isInitializing = true;
      const item = this.existingItem;
      const desc =
        item.isCustom && item.taskName.includes(':')
          ? item.taskName.split(': ')[1].trim()
          : '';
      let criteriaName = '';
      let type = item.type || (item.isCustom ? item.taskName.split(': ')[0] : 'Ticket');
      this.form.type = type;
      this.form.reason = item.scoreReason || '';
      this.form.project = item.project || item.board || '';
      const board = this.boards.find(b => b.name === this.form.project);
      this.form.boardId = board ? board.id : '';
      this.form.prs = Array.isArray(item.details)
        ? item.details.map(d => ({ link: typeof d === 'object' ? d.link : d }))
        : [];
      if (type === 'Ticket') {
        this.form.title = item.taskName || '';
        this.form.url = item.link || '';
        this.form.duration = parseHours(item.durationSpent) || 0;
        this.form.reopens = item.reopened || 0;
        this.form.deadlineMissed = item.deadlineMissed || 0;
        const hrs = this.form.duration;
        const matching = this.criteriaList.find(c => c.type === 'Ticket' && (hrs >= (c.rules.duration.min || 0) && hrs < (c.rules.duration.max || Infinity)));
        if (matching) criteriaName = matching.name;
      } else if (type === 'Interview') {
        this.form.level = desc || '';
        const intCrit = this.criteriaList.find(c => c.type === 'Interview' && c.rules.level === this.form.level);
        if (intCrit) criteriaName = intCrit.name;
      } else {
        const crit = this.criteriaList.find(c => c.type === type);
        if (crit) criteriaName = crit.name;
      }
      this.form.criteriaName = criteriaName;
      const selected = this.criteriaList.find(c => c.name === criteriaName);
      if (selected) this.form.selectedCriteria = selected;
      switch (type) {
        case 'Weekly Session':
          this.form.value = item.rating != null ? item.rating : 1;
          if (desc) {
            const [mm, dd, yyyy] = desc.split('/');
            this.form.date = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
          } else {  
            this.form.date = '';
          }
          break;
        case 'Weekly Session Attendance':
        case 'Random Post':
          this.form.count = parseInt(desc) || 0;
          break;
        case 'Lead Per Project':
          this.form.project = desc || '';
          const b = this.boards.find(b => b.name === desc);
          this.form.boardId = b ? b.id : '';
          break;
      }

      this.$nextTick(() => {
        this.originalFormSnapshot = JSON.parse(JSON.stringify(this.form));
        this.isInitializing = false;
        console.log('Original form snapshot:', this.originalFormSnapshot);
      });
    }
  },
  methods: {
    async fetchCriteria() {
      try {
        const list = await firebase.getAllPerformanceCriteria();
        console.log("performance critteria", list)
        this.criteriaList = list || [];
      } catch (err) {
        console.error(err);
      }
    },
    handleCriteriaChange(value) {
      const selected = this.criteriaList.find(c => c.name === value);
      if (selected) {
        this.form.selectedCriteria = selected;
        this.form.type = selected.type;
        this.form.criteriaName = value;
        this.showTypeError = false;
        this.form.reason = '';
        if (selected.type === 'Ticket') {
          this.form.title = '';
        } else {
          this.form.title = this.autoGeneratedTitle;
        }
        if (selected.type === 'Interview') {
          this.form.level = selected.rules.level;
        }
        this.resetDependentFields();
        this.updateAutoTitle();
      }
    },
    isValidUrl(url) {
      if (!url.trim()) return false;
      try {
        new URL(url);
        return true;
      } catch (e) {
        return false;
      }
    },
    resetDependentFields() {
      this.form.prs = [];
      this.form.count = 0;
      this.form.value = 1;
      this.form.date = '';
      this.form.level = '';
      this.form.project = '';
      this.form.boardId = '';
      this.form.url = '';
      this.form.duration = 0;
      this.form.reopens = 0;
      this.form.deadlineMissed = 0;
      if (this.form.type === 'Weekly Session Attendance') {
        this.form.count = 1;
      }
      if (this.form.type === 'Weekly Session') {
        this.form.value = 1;
      }
      if (this.form.type === 'Random Post') {
        if (this.hasLimit) {
          this.form.count = this.limitValue;
        } else {
          this.form.count = this.rules.countDivisor ;
        }
      }
    },
    resetForm() {
      this.form = {
        criteriaName: '',
        type: '',
        title: '',
        project: '',
        boardId: '',
        url: '',
        duration: 0,
        reopens: 0,
        deadlineMissed: 0,
        count: 0,
        value: 1,
        date: '',
        level: '',
        reason: '',
        prs: [],
        selectedCriteria: null
      };
      this.originalFormSnapshot = null;
      this.formSubmitted = false;
      this.showTypeError = false;
    },
    updateAutoTitle() {
      if (this.form.type !== 'Ticket') {
        this.form.title = this.autoGeneratedTitle;
      }
    },
    handleProjectChange(value) {
      const selected = this.boards.find(b => b.name === value);
      this.form.project = value;
      this.form.boardId = selected.id || null;
    },
    addPr() { this.form.prs.push({ link: '' }); },
    removePr(i) { this.form.prs.splice(i, 1); },
    preventDecimalInput(e) {
      if (e.key === '.' || e.key === ',') {
        e.preventDefault();
      }
    },
    hasChanges() {
      if (!this.originalFormSnapshot) return true;
      const currentPrs = JSON.stringify(this.form.prs.map(p => p.link.trim()).sort());
      const originalPrs = JSON.stringify(this.originalFormSnapshot.prs.map(p => p.link.trim()).sort());
      return (
        this.form.type !== this.originalFormSnapshot.type ||
        this.form.title !== this.originalFormSnapshot.title ||
        this.form.project !== this.originalFormSnapshot.project ||
        this.form.url !== this.originalFormSnapshot.url ||
        this.form.duration !== this.originalFormSnapshot.duration ||
        this.form.reopens !== this.originalFormSnapshot.reopens ||
        this.form.deadlineMissed !== this.originalFormSnapshot.deadlineMissed ||
        this.form.count !== this.originalFormSnapshot.count ||
        this.form.value !== this.originalFormSnapshot.value ||
        this.form.date !== this.originalFormSnapshot.date ||
        this.form.level !== this.originalFormSnapshot.level ||
        this.form.reason !== this.originalFormSnapshot.reason ||
        currentPrs !== originalPrs
      );
    },
    validateForm() {
      this.formSubmitted = true;
      this.showTypeError = !this.form.criteriaName;
      if (this.existingItem && !this.hasChanges()) {
        Swal("No Changes", "No changes detected. Please modify something before updating.", "warning");
        return false;
      }

      if (!this.form.criteriaName) {
        Swal("Required", "Please select a type.", "error");
        return false;
      }
      if (this.form.type === 'Ticket' && !this.form.title.trim()) {
        Swal("Required", "Title is required for Ticket.", "error");
        return false;
      }
      if (['Ticket', 'Lead Per Project'].includes(this.form.type) && !this.form.project.trim()) {
        Swal("Required", "Project is required.", "error");
        return false;
      }
      if (this.form.type === 'Ticket' && !this.form.url.trim()) {
        Swal("Required", "Ticket URL is required.", "error");
        return false;
      }
      if (this.form.type === 'Ticket' && !this.isValidUrl(this.form.url)) {
        Swal("Invalid", "Please enter a valid Ticket URL.", "error");
        return false;
      }
      if (this.form.type === 'Ticket' && (this.form.duration === null || this.form.duration === 0)) {
        Swal("Required", "Duration (hrs) is required for Ticket.", "error");
        return false;
      }
      if (this.form.type === 'Ticket') {
        const selected = this.form.selectedCriteria;
        const hrs = this.form.duration || 0;
        const min = selected.rules.duration.min || 0;
        const max = selected.rules.duration.max - 0.01 || 0;
        if (hrs < min || hrs > max) {
          Swal("Invalid", `Duration must be between ${min} and ${max} hrs for "${selected.name}".`, "error");
          return false;
        }
      }
      if (this.form.type === 'PR Review/Help' && this.form.prs.length === 0) {
        Swal("Required", "Please add PR/Help.", "error");
        return false;
      }
      if (this.form.type === 'PR Review/Help' && !this.hasLimit && this.form.prs.length % this.prDivisor !== 0) {
        Swal("Invalid", `Number of PRs/Helps must be divisible by ${this.prDivisor}.`, "error");
        return false;
      }
      if (this.form.type === 'PR Review/Help' && this.form.prs.some(pr => !pr.link.trim())) {
        Swal("Required", "Please fill all PR/Help details.", "error");
        return false;
      }
      if (this.form.type === 'Random Post' && this.form.count <= 0) {
        Swal("Invalid", "Number of posts must be greater than 0.", "error");
        return false;
      }
      if (this.form.type === 'Random Post' && !this.hasLimit && this.form.count % this.randomDivisor !== 0) {
        Swal("Invalid", `Number of posts must be divisible by ${this.randomDivisor}.`, "error");
        return false;
      }
      if (this.form.type === 'Weekly Session Attendance' && this.form.count <= 0) {
        Swal("Invalid", "Sessions attended must be greater than 0.", "error");
        return false;
      }
      if (this.form.type === 'Weekly Session') {
        if (!this.form.date) {
          Swal("Required", "Date is required for Weekly Session.", "error");
          return false;
        }
        if (this.form.value == null || this.form.value < 1 || this.form.value > 5) {
          Swal("Invalid", "Please enter a valid rating (1-5) for Weekly Session.", "error");
          return false;
        }
      }
      if (this.form.type === 'Lead Per Project' && !this.form.project) {
        Swal("Required", "Please select a project.", "error");
        return false;
      }
      return true;
    },
    addChangeIfDifferent(changes, label, oldVal, newVal) {
      const normalizedOld = oldVal == null || oldVal === '' ? '(none)' : oldVal;
      const normalizedNew = newVal == null || newVal === '' ? '(none)' : newVal;

      if (normalizedOld !== normalizedNew) {
        changes[label] = `${normalizedOld} → ${normalizedNew}`;
      }
    },
    async handleSubmit() {
      if (!this.validateForm()) return;

      this.isLoading = true;

      try {
        const payload = {
          user: this.employee.uid,
          year: this.year,
          month: this.month,
          name: this.displayTitle,
          type: this.form.type,
          link: this.form.url || null,
          score: this.calcScore,
          scoreReason: this.form.reason || null,
          boardName: this.form.project || null,
          boardId: this.form.boardId || null,
          board: this.form.project || null,
          details:
            this.form.type === "PR Review/Help"
              ? this.form.prs
              : null,
          rating: this.form.type === 'Weekly Session' ? this.form.value : null,
          ticketStatus: this.isAdmin ? "approved" : "pending",
          status: "approved",
          deadlineMissedCount : this.form.deadlineMissed || null,
          durationInProgress : this.form.duration || null,
          reopenCount : this.form.reopens || null,
          isCustom: true,
          filterKey: `${this.year}-${this.month}-${this.employee.uid}-${this.form.boardId || "null"}`,
        };
        if (this.existingItem) {
            const cardId = this.existingItem.id;
            const existing = this.$parent.editedCards[cardId] || {};
            const lastKnownData = {
              ... (existing.originalData || {}),
              ... (existing.updatedData || {})
            };
            const originalData = existing.originalData || {
              name: this.existingItem.taskName || null,
              link: (this.existingItem && this.existingItem.link) || null,
              durationInProgress: this.existingItem.durationSpent,
              reopenCount: this.existingItem.reopened,
              score: this.existingItem.score,
              deadlineMissedCount: this.existingItem.deadlineMissed,
              boardName: this.existingItem.project,
              details: this.existingItem.details || null,
              type: this.existingItem.type || null,
              scoreReason: this.existingItem.scoreReason || null,
            };

            const updatedData = {
              ...existing.updatedData || {},
              name: payload.name,
              link: payload.link || null,
              durationInProgress: payload.durationInProgress,
              reopened: payload.reopenCount,
              score: payload.score,
              deadlineMissedCount: payload.deadlineMissedCount,
              scoreReason: payload.scoreReason,
              boardName: payload.boardName,
              rating: payload.rating,
              status: this.isAdmin ? "approved" : null,
              details: payload.details || null,
            };
            const editPayload = {
              cardId: existing.cardId || cardId,
              isCustom: existing.isCustom == true ? true : null,
              type: payload.type || null,
              user: this.employee.uid,
              year: this.year,
              month: this.month,
              board: this.form.project || null,
              movedDate:this.existingItem && this.existingItem.movedDate,
              movedTime :this.existingItem && this.existingItem.movedTime,
              originalData,
              updatedData,
              scoreReason: payload.scoreReason || null,
              ticketStatus: this.isAdmin ? "approved" : "pending",
              status: this.isAdmin ? "approved" : "pending",
              customEdit: existing.isCustom == true ? true : null,
            };
            await firebase.saveEditedCard(editPayload);
            const changes = {};
            this.addChangeIfDifferent(changes, 'Title', lastKnownData.name, payload.name);
            this.addChangeIfDifferent(changes, 'Type', lastKnownData.type, payload.type);
            this.addChangeIfDifferent(changes, 'Score', lastKnownData.score || 0, payload.score);
            this.addChangeIfDifferent(changes, 'Reason', lastKnownData.scoreReason || '', payload.scoreReason || '');
            if (payload.type === 'Ticket') {
              this.addChangeIfDifferent(changes, 'Project', lastKnownData.boardName, payload.boardName);
              this.addChangeIfDifferent(changes, 'Ticket URL', lastKnownData.link, payload.link);
              const oldDurationFormatted = lastKnownData.durationInProgress != null ? 
                formatDuration(parseDurationString(lastKnownData.durationInProgress)) : 'N/A';
              const newDurationFormatted = payload.durationInProgress != null ? 
                formatDuration(parseDurationString(payload.durationInProgress.toString())) : 'N/A';
              if (oldDurationFormatted !== newDurationFormatted) {
                changes['Duration (hrs)'] = `${oldDurationFormatted} → ${newDurationFormatted}`;
              }
              
              this.addChangeIfDifferent(changes, 'Reopens', lastKnownData.reopenCount || 0, payload.reopenCount || 0);
              this.addChangeIfDifferent(changes, 'Deadline Missed', lastKnownData.deadlineMissedCount || 0, payload.deadlineMissedCount || 0);
            } else if (payload.type === 'PR Review/Help') {
              const oldPrs = Array.isArray(lastKnownData.details) ? lastKnownData.details : [];
              const newPrs = Array.isArray(payload.details) ? payload.details : [];
              if (oldPrs.length !== newPrs.length) {
                changes['PRs_Helps_Count'] = `${oldPrs.length} → ${newPrs.length}`;
              }
              const maxLength = Math.max(oldPrs.length, newPrs.length);
              let prChanges = [];
              
              for (let i = 0; i < maxLength; i++) {
                const oldLink = ((oldPrs[i] && oldPrs[i].link) || '').trim() || '(empty)';
                const newLink = ((newPrs[i] && newPrs[i].link) || '').trim() || '(empty)';

                if (oldLink !== newLink) {
                  prChanges.push(`PR ${i + 1}: ${oldLink} → ${newLink}`);
                }
              }

              if (prChanges.length > 0) {
                changes['PRs_Helps_Updated'] = prChanges.join('\n');
              }
            } 
            else if (payload.type === 'Weekly Session') {
              this.addChangeIfDifferent(changes, 'Rating', lastKnownData.rating || 1, this.form.value);
              const oldDateDesc = lastKnownData.name && lastKnownData.name.includes(': ') ? 
                lastKnownData.name.split(': ')[1].trim() : '(none)';
              const newDateDesc = this.form.date ? new Date(this.form.date).toLocaleDateString('en-US') : '(none)';
              if (oldDateDesc !== newDateDesc) {
                changes['Date'] = `${oldDateDesc} → ${newDateDesc}`;
              }
            } else if (['Weekly Session Attendance', 'Random Post'].includes(payload.type)) {
              const oldCountDesc = lastKnownData.name && lastKnownData.name.includes(': ') ? 
                parseInt(lastKnownData.name.split(': ')[1].trim()) || 0 : 0;
              if (oldCountDesc !== this.form.count) {
                changes['Count'] = `${oldCountDesc} → ${this.form.count}`;
              }
            } else if (payload.type === 'Interview') {
              const oldLevel = lastKnownData.name && lastKnownData.name.includes(': ') ? 
                lastKnownData.name.split(': ')[1].trim() : '(none)';
              if (oldLevel !== this.form.level) {
                changes['Level'] = `${oldLevel} → ${this.form.level}`;
              }
            } else if (payload.type === 'Lead Per Project') {
              const oldProject = lastKnownData.name && lastKnownData.name.includes(': ') ? 
                lastKnownData.name.split(': ')[1].trim() : '(none)';
              this.addChangeIfDifferent(changes, 'Project', lastKnownData.boardName || oldProject, payload.boardName);
            }

            console.log('Final changes object:', changes); // Debug log

            const action = this.isAdmin ? "Updated" : " Updated Request";
            const commonField = {
              description: `${this.$store.state.auth.user.email} ${action.toLowerCase()} performance item "${this.displayTitle}" for ${this.employee.email} with score ${this.calcScore} ${this.form.reason ? '. Reason: ' + this.form.reason : ''}`,
              activityName: `${this.$store.state.auth.user.username} ${action.toLowerCase()} performance item "${this.displayTitle}" for ${this.month} ${this.year}`,
            }
            const adminActivity = {
              adminId: this.$store.state.auth.user.uid,
              activityOnUser: this.employee.uid,
              activityOnUserName: this.employee.username,
              activityByUserName: this.$store.state.auth.user.username || 'Admin',
              action,
              activityTitle: `${action.toLowerCase()} performance item`,
              activityDescription: commonField.description,
              activityName: commonField.activityName,
              type: 'performance_edit',
              timestamp: new Date().toLocaleString('en-PK'),
              cardId,
              cardName: this.displayTitle,
              field: "Task",
              oldValue: lastKnownData.score || 0,
              newValue: this.calcScore,
              reason: this.form.reason || null,
              month: this.month,
              year: this.year,
              actionType: action.toLowerCase(),
              module: 'performance',
              entityName: 'item',
              entityId: cardId,
              previousData: removeNullFields(lastKnownData),
              newData: removeNullFields(updatedData),
              change: changes, 
              performedBy: this.$store.state.auth.user.uid,
              userRole: this.isAdmin ? 'admin' : 'user',
              source: 'UI'
            };
            await logAdminActivity(adminActivity);

            const { username, uid } = this.$store.state.auth.user;
          await handleActivityNotification(
            {
              type: "performance_edit",
              activityDescription: commonField.description,
              activityName: commonField.activityName,
              activityOnUser: this.employee.uid,
              activityOnUserName: this.employee.username,
              activityByUserName: username,
              action: action,
              newValue: this.calcScore,
              cardName: this.displayTitle,
            },
            this.$store.state.auth,
            uid
          );

            Swal("Success!", "Item updated successfully.", "success");
            this.$emit("updated", editPayload);
        } else {
          const cardId = await firebase.addPerformanceItem(payload);
          const action = this.isAdmin ? "Added" : "Requested";
          const commonField = {
            description: `${this.$store.state.auth.user.email} ${action.toLowerCase()} a new performance item "${this.displayTitle}" for ${this.employee.email} with score ${this.calcScore} ${this.form.reason ? '. Reason: ' + this.form.reason : ''}`,
            activityName: `${this.$store.state.auth.user.username} ${action.toLowerCase()} a new performance item "${this.displayTitle}" for ${this.month} ${this.year}`,
          }
          const adminActivity = {
            adminId: this.$store.state.auth.user.uid,
            activityOnUser: this.employee.uid,
            activityOnUserName: this.employee.username,
            activityByUserName: this.$store.state.auth.user.username || 'Admin',
            action,
            activityTitle: `${action.toLowerCase()} performance item`,
            activityDescription: commonField.description,
            activityName: commonField.activityName,
            type: 'performance_edit',
            timestamp: new Date().toLocaleString('en-PK'),
            cardId,
            cardName: this.displayTitle,
            field: "Task",
            oldValue: null,
            newValue: this.calcScore,
            reason: this.form.reason || null,
            month: this.month,
            year: this.year,
            actionType: action.toLowerCase(),
            module: 'performance',
            entityName: 'item',
            entityId: cardId,
            previousData: null,
            newData: removeNullFields(payload),
            performedBy: this.$store.state.auth.user.uid,
            userRole: this.isAdmin ? 'admin' : 'user',
            source: 'UI'
          };

          await logAdminActivity(adminActivity);

          const { username, uid } = this.$store.state.auth.user;
          await handleActivityNotification(
            {
              type: "performance_edit",
              activityDescription: commonField.description,
              activityName: commonField.activityName,
              activityOnUser: this.employee.uid,
              activityOnUserName: this.employee.username,
              activityByUserName: username,
              action: action,
              newValue: this.calcScore,
              cardName: this.displayTitle,
            },
            this.$store.state.auth,
            uid
          );

          Swal("Success!", "Item saved successfully.", "success");
          this.$emit("submitted", {cardId, payload});
        }
        this.resetForm();
        this.$emit('update:modelValue', false);

      } catch (error) {
        console.error(error);
        Swal("Error", "Failed to submit item.", "error");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.form-control,
.form-select {
  height: 42px;
  padding: 8px 12px;
}

.input-group .form-control {
  height: 42px;
}
.btn-sm {
  height: 36px;
  font-size: 0.875rem;
  background-color: #435851;
}
</style>


// WEBPACK FOOTER //
// src/components/Performance/AddPerformanceItemModal.vue